import axios from 'axios';
import { getApiBaseUrl } from '../utils/urlHelper';

const API = `${getApiBaseUrl()}/api`;

export const api = {
  // Tours
  getTours: (params = {}) => axios.get(`${API}/tours`, { params }),
  getTour: (id) => axios.get(`${API}/tours/${id}`),
  // Admin variant: returns ANY tour (active or not, public or not, archived or not).
  // Used by the admin edit form so that private tours can still be loaded.
  getAdminTour: (id, config) => axios.get(`${API}/admin/tours/${id}`, config),
  
  // Admin Tours
  getAllToursAdmin: (config) => axios.get(`${API}/admin/tours`, config),
  getToursPicker: (config) => axios.get(`${API}/admin/tours/picker`, config),
  createTour: (data, config) => axios.post(`${API}/admin/tours`, data, config),
  updateTour: (id, data, config) => axios.put(`${API}/admin/tours/${id}`, data, config),
  deleteTour: (id, config) => axios.delete(`${API}/admin/tours/${id}`, config),
  
  // Bookings
  createBooking: (data, config) => axios.post(`${API}/bookings`, data, config),
  uploadBookingPassport: (bookingId, formData, authHeaders = {}) => axios.post(`${API}/bookings/${bookingId}/upload-passport`, formData, { headers: { 'Content-Type': 'multipart/form-data', ...authHeaders } }),
  uploadBookingDoc: (bookingId, formData, authHeaders = {}) => axios.post(`${API}/bookings/${bookingId}/upload-doc`, formData, { headers: { 'Content-Type': 'multipart/form-data', ...authHeaders } }),
  
  // Admin Bookings
  getBookings: (params, config) => axios.get(`${API}/admin/bookings`, { params, ...config }),
  getBookingsStats: (config) => axios.get(`${API}/admin/bookings/stats`, config),
  getBookingDetail: (id, config) => axios.get(`${API}/admin/bookings/${id}`, config),
  getBooking: (id, config) => axios.get(`${API}/admin/bookings/${id}`, config),
  updateBookingStatus: (id, status, config, extra = {}) => axios.put(`${API}/admin/bookings/${id}/status`, { status, ...extra }, config),
  deleteBooking: (id, config) => axios.delete(`${API}/admin/bookings/${id}`, config),
  releasePayment: (id, config) => axios.put(`${API}/admin/bookings/${id}/release-payment`, {}, config),
  // Iter 277 — Multi-room bookings (opt-in per booking)
  setBookingRooms: (id, rooms, config) => axios.patch(`${API}/admin/bookings/${id}/rooms`, { rooms }, config),
  clearBookingRooms: (id, config) => axios.delete(`${API}/admin/bookings/${id}/rooms`, config),
  getRoomingList: (tourId, config) => axios.get(`${API}/admin/tours/${tourId}/rooming-list`, config),
  saveFullRoomingList: (tourId, roomingData, config) => axios.put(`${API}/admin/tours/${tourId}/save-rooming-list`, { rooming_data: roomingData }, config),
  resetRoomingList: (tourId, config) => axios.delete(`${API}/admin/tours/${tourId}/reset-rooming-list`, config),

  // Client Detail & Financial
  getClientDetail: (clientId, config) => axios.get(`${API}/admin/clients/${clientId}/detail`, config),
  getPaymentSettings: (config) => axios.get(`${API}/admin/payment-settings`, config),
  updatePaymentSettings: (data, config) => axios.put(`${API}/admin/payment-settings`, data, config),
  sendPaymentInfo: (bookingId, data, config) => axios.post(`${API}/admin/bookings/${bookingId}/send-payment-info`, data, config),
  scheduleReminders: (bookingId, data, config) => axios.post(`${API}/admin/bookings/${bookingId}/schedule-reminders`, data, config),
  getScheduledReminders: (config) => axios.get(`${API}/admin/scheduled-reminders`, config),
  deleteReminder: (id, config) => axios.delete(`${API}/admin/scheduled-reminders/${id}`, config),

  // Admin create client
  createClientAdmin: (data, config, opts = {}) => {
    const url = opts.force ? `${API}/admin/clients/create?force=true` : `${API}/admin/clients/create`;
    return axios.post(url, data, config);
  },
  createClient: (data, config, opts = {}) => {
    const url = opts.force ? `${API}/admin/clients/create?force=true` : `${API}/admin/clients/create`;
    return axios.post(url, data, config);
  },
  
  // Dashboard
  getDashboardStats: (config, view = 'active') => axios.get(`${API}/admin/dashboard/stats?view=${view}`, config),
  getToursStats: (config, view = 'active', tourId = '') => axios.get(`${API}/admin/dashboard/tours?view=${view}${tourId ? `&tour_id=${tourId}` : ''}`, config),
  
  // FAQs
  getFAQs: () => axios.get(`${API}/faqs`),
  getAllFAQsAdmin: (config) => axios.get(`${API}/admin/faqs`, config),
  createFAQ: (data, config) => axios.post(`${API}/admin/faqs`, data, config),
  updateFAQ: (id, data, config) => axios.put(`${API}/admin/faqs/${id}`, data, config),
  deleteFAQ: (id, config) => axios.delete(`${API}/admin/faqs/${id}`, config),
  // Iter 371d — Drag-and-drop reorder + public open-count analytics.
  reorderFAQs: (ids, config) => axios.post(`${API}/admin/faqs/reorder`, { ids }, config),
  trackFAQView: (id) => axios.post(`${API}/faqs/${id}/view`).catch(() => null),
  seedCanonicalFAQs: (config) => axios.post(`${API}/admin/faqs/seed-canonical`, {}, config),
  
  // Payment
  getPaymentInfo: () => axios.get(`${API}/payment/info`),
  createPayment: (data) => axios.post(`${API}/payment/create`, data),
  uploadPaymentProof: (data) => axios.post(`${API}/payment/upload-proof`, data),
  approvePayment: (bookingId, paymentIndex, config) => axios.post(`${API}/admin/payment/approve/${bookingId}?payment_index=${paymentIndex}`, {}, config),
  rejectPayment: (bookingId, paymentIndex, reason, config) => axios.post(`${API}/admin/payment/reject/${bookingId}?payment_index=${paymentIndex}`, { reason }, config),

  // File Upload
  uploadFile: (file, config, opts = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/admin/upload${opts.photo ? '?optimize=photo' : ''}`, formData, {
      ...config,
      headers: { Authorization: config?.headers?.Authorization }
    });
  },

  // Client Auth
  clientRegister: (data) => axios.post(`${API}/client/register`, data),
  clientLogin: (data) => axios.post(`${API}/client/login`, data),
  clientBookings: (config, lang) => axios.get(`${API}/client/my-bookings${lang ? `?lang=${lang}` : ''}`, config),
  clientProfile: (config) => axios.get(`${API}/client/profile`, config),
  // iter Group Leaders Phase 2 — tours where the client is in `tour.leaders[]`
  clientMyGroups: (config) => axios.get(`${API}/client/my-groups`, config),

  // Client Payments
  getPaymentConfig: (config) => axios.get(`${API}/client/payment/config`, config),
  getBookingPaymentInfo: (bookingId, config) => axios.get(`${API}/client/booking/${bookingId}/payment-info`, config),
  createClientPayment: (data, config) => axios.post(`${API}/client/payment/create`, data, config),

  // Admin tour bookings
  getTourBookings: (tourId, config) => axios.get(`${API}/admin/tours/${tourId}/bookings`, config),

  // Admin email
  sendEmail: (data, config) => axios.post(`${API}/admin/email/send`, data, config),
  sendEmailWithAttachments: (formData, config) => axios.post(`${API}/admin/email/send-with-attachments`, formData, config),
  getEmailDispatch: (id, config) => axios.get(`${API}/admin/email/dispatches/${id}`, config),
  getLatestEmailDispatch: (config) => axios.get(`${API}/admin/email/dispatches/latest`, config),
  retryFailedEmailDispatch: (id, config) => axios.post(`${API}/admin/email/dispatches/${id}/retry-failed`, {}, config),
  getGmailStatus: (config) => axios.get(`${API}/admin/gmail/status`, config),
  startGmailOAuth: (config) => axios.post(`${API}/admin/gmail/oauth/start`, {}, config),
  checkGmailConnectivity: (config) => axios.post(`${API}/admin/gmail/connectivity-check`, {}, config),
  getEmailRecipients: (tourId, config, includeCancelled = false) => axios.get(`${API}/admin/email/recipients?${tourId ? `tour_id=${tourId}&` : ''}include_cancelled=${includeCancelled ? 'true' : 'false'}`, config),

  // Admin clients
  searchClients: (q, config) => axios.get(`${API}/admin/clients/search?q=${encodeURIComponent(q)}`, config),
  importClients: (file, config) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/admin/clients/import`, formData, {
      ...config,
      headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' }
    });
  },
  listClients: (params, config) => axios.get(`${API}/admin/clients`, { params, ...config }),
  updateClient: (clientId, data, config) => axios.put(`${API}/admin/clients/${clientId}`, data, config),
  deleteClient: (clientId, config) => axios.delete(`${API}/admin/clients/${clientId}`, config),
  getClientDocuments: (clientId, config) => axios.get(`${API}/admin/clients/${clientId}/documents`, config),
  uploadClientDocument: (clientId, formData, config, docType) => axios.post(`${API}/admin/clients/${clientId}/documents${docType ? `?doc_type=${docType}` : ''}`, formData, config),
  setClientDocumentType: (clientId, docId, docType, config) => axios.patch(`${API}/admin/clients/${clientId}/documents/${docId}`, { doc_type: docType }, config),
  deleteClientDocument: (clientId, docId, config) => axios.delete(`${API}/admin/clients/${clientId}/documents/${docId}`, config),
  renameClientDocument: (clientId, docId, newName, config) => axios.patch(`${API}/admin/clients/${clientId}/documents/${docId}`, { original_filename: newName }, config),
  updateClientDocument: (clientId, docId, formData, config) => axios.put(`${API}/admin/clients/${clientId}/documents/${docId}`, formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' }
  }),
  uploadClientPhoto: (clientId, formData, config) => axios.post(`${API}/admin/clients/${clientId}/photo`, formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' }
  }),
  uploadMyPhoto: (formData, config) => axios.post(`${API}/client/photo`, formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' }
  }),
  deleteMyPhoto: (config) => axios.delete(`${API}/client/photo`, config),
  sendMarketingEmail: (data, config) => axios.post(`${API}/admin/email/marketing`, data, config),
  sendPaymentBulkEmail: (data, config) => axios.post(`${API}/admin/email/send-payment`, data, config),

  // Client documents
  uploadDocument: (file, docType, config) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/client/documents/upload?doc_type=${docType}`, formData, {
      ...config,
      headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' }
    });
  },
  getMyDocuments: (config) => axios.get(`${API}/client/documents`, config),
  deleteDocument: (docId, config) => axios.delete(`${API}/client/documents/${docId}`, config),

  // Gallery
  getGallery: () => axios.get(`${API}/gallery`),
  createGalleryImage: (data, config) => axios.post(`${API}/admin/gallery`, data, config),
  updateGalleryImage: (id, data, config) => axios.put(`${API}/admin/gallery/${id}`, data, config),
  deleteGalleryImage: (id, config) => axios.delete(`${API}/admin/gallery/${id}`, config),
  reorderGallery: (data, config) => axios.put(`${API}/admin/gallery/reorder`, data, config),

  // Translation
  translateText: (data, config) => axios.post(`${API}/admin/translate`, data, config),
  translateList: (data, config) => axios.post(`${API}/admin/translate-list`, data, config),
  translateBatch: (data, config) => axios.post(`${API}/admin/translate-batch`, data, config),

  // Payment Methods CRUD
  getPaymentMethods: (config) => axios.get(`${API}/admin/payment-methods`, config),
  createPaymentMethod: (data, config) => axios.post(`${API}/admin/payment-methods`, data, config),
  updatePaymentMethod: (id, data, config) => axios.put(`${API}/admin/payment-methods/${id}`, data, config),
  deletePaymentMethod: (id, config) => axios.delete(`${API}/admin/payment-methods/${id}`, config),

  // Admin Booking Management
  adminCreateBooking: (data, config) => axios.post(`${API}/admin/bookings/create`, data, config),
  editBooking: (bookingId, data, config) => axios.put(`${API}/admin/bookings/${bookingId}/edit`, data, config),
  recordPayment: (data, config) => axios.post(`${API}/admin/payments/record`, data, config),
  recordPaymentAdjustment: (data, config) => axios.post(`${API}/admin/payments/adjustment`, data, config),
  applyDiscount: (data, config) => axios.post(`${API}/admin/payments/discount`, data, config),
  applySurcharge: (data, config) => axios.post(`${API}/admin/payments/surcharge`, data, config),
  // Iter 321h — Stripe checkout shortlinks (para evitar quebra em WhatsApp mobile)
  createStripeShortlink: (url, config) => axios.post(`${API}/admin/shortlinks/stripe`, { url }, config),
  voidPayment: (data, config) => axios.post(`${API}/admin/payments/void`, data, config),
  getReceipt: (bookingId, params, config) => axios.get(`${API}/admin/bookings/${bookingId}/receipt`, { params, ...config }),
  sendReceipt: (bookingId, data, config) => axios.post(`${API}/admin/bookings/${bookingId}/send-receipt`, data, config),
  getTourFinancials: (tourId, config) => axios.get(`${API}/admin/tours/${tourId}/financials`, config),

  // Archive Tours
  archiveTour: (tourId, config) => axios.put(`${API}/admin/tours/${tourId}/archive`, {}, config),
  reorderTours: (order, config) => axios.put(`${API}/admin/tours/reorder`, order, config),
  updateRoomingList: (tourId, edits, config) => axios.put(`${API}/admin/tours/${tourId}/rooming-list`, edits, config),
  getTourQRCode: (tourId, config) => `${API}/admin/tours/${tourId}/qrcode`,

  // Reports
  generateReport: (data, config) => axios.post(`${API}/admin/reports`, data, config),

  // Email Templates
  getBookingEmailTemplate: (config) => axios.get(`${API}/admin/email-templates/booking-confirmation`, config),
  updateBookingEmailTemplate: (data, config) => axios.put(`${API}/admin/email-templates/booking-confirmation`, data, config),
  uploadEmailImage: (formData, config) => axios.post(`${API}/admin/email-templates/upload-image`, formData, { ...config, headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' } }),

  // Contracts
  getContracts: (config) => axios.get(`${API}/admin/contracts`, config),
  getContract: (id, config) => axios.get(`${API}/admin/contracts/${id}`, config),
  uploadContract: (formData, config) => axios.post(`${API}/admin/contracts/upload`, formData, { ...config, headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' } }),
  updateContractFields: (id, data, config) => axios.put(`${API}/admin/contracts/${id}/fields`, data, config),
  toggleContractActive: (id, config) => axios.put(`${API}/admin/contracts/${id}/activate`, {}, config),
  deleteContract: (id, config) => axios.delete(`${API}/admin/contracts/${id}`, config),
  getContractSignatures: (config) => axios.get(`${API}/admin/contracts/signatures`, config),

  // Client Contract Signing
  getContractForSigning: (token) => axios.get(`${API}/contracts/sign/${token}`),
  signContract: (token, data) => axios.post(`${API}/contracts/sign/${token}`, data),
  requestNewContractLink: (ref) => axios.post(`${API}/contracts/request-new-link`, { ref }),
  resendContract: (bookingId, config) => axios.post(`${API}/admin/contracts/resend/${bookingId}`, {}, config),

  // Hotels
  getAdminHotels: (config) => axios.get(`${API}/admin/hotels`, config),
  getTourHotels: (tourId) => axios.get(`${API}/tours/${tourId}/hotels`),

  // User Management
  getUsers: (config) => axios.get(`${API}/admin/users`, config),
  getUser: (id, config) => axios.get(`${API}/admin/users/${id}`, config),
  createUser: (data, config) => axios.post(`${API}/admin/users`, data, config),
  updateUser: (id, data, config) => axios.put(`${API}/admin/users/${id}`, data, config),
  deleteUser: (id, config) => axios.delete(`${API}/admin/users/${id}`, config),
  getPagesList: (config) => axios.get(`${API}/admin/pages-list`, config),
  searchLinkClients: (q, config) => axios.get(`${API}/admin/users/search-link-clients`, { params: { q: q || '' }, ...config }),
  getAuthModes: (config) => axios.get(`${API}/admin/auth/modes`, config),
  switchToGuide: (config) => axios.post(`${API}/admin/auth/switch-to-guide`, {}, config),
  getPipeline: (config, view = 'active') => axios.get(`${API}/admin/pipeline?view=${view}`, config),

  // Audit Log
  getAuditLog: (params, config) => axios.get(`${API}/admin/audit-log`, { params, ...config }),

  // Google Places API - Address Autocomplete
  createPlacesSession: () => axios.post(`${API}/places/session`),
  placesAutocomplete: (data) => axios.post(`${API}/places/autocomplete`, data),
  getPlaceDetails: (placeId, sessionToken) => axios.get(`${API}/places/details/${placeId}`, { params: { session_token: sessionToken } }),

  // Iter 328 — Standalone group organizer leads
  submitGenericGroupLead: (data) => axios.post(`${API}/group-leads/generic`, data),
};